// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useState } from 'react'
import { ExternalLink, Globe, Search, Sparkles, Store } from 'lucide-react'
import type { LayoutReference } from '@storefronts/common-types'
import { groupLayoutReferencesByCategory } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogCloseButton,
    DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { XSmall } from '@/components/ui/typography'
import { useLandingPagesQuery } from '@/hooks/landingPageApiHooks'
import { useLayoutReferencesQuery } from '@/hooks/useLayoutReferences'
import { cn } from '@/lib/utils'

export type TemplatePickerResult =
    | { kind: 'referenceUrl'; url: string; title?: string }
    | { kind: 'sourceStorefront'; landingPageHash: string; name: string }

interface TemplatePickerModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelected: (result: TemplatePickerResult) => void
    currentReferenceUrl?: string
    currentSourceStorefrontHash?: string
}

export function TemplatePickerModal({
    open,
    onOpenChange,
    onSelected,
    currentReferenceUrl,
    currentSourceStorefrontHash,
}: TemplatePickerModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {open && (
                <TemplatePickerContent
                    onOpenChange={onOpenChange}
                    onSelected={onSelected}
                    currentReferenceUrl={currentReferenceUrl}
                    currentSourceStorefrontHash={currentSourceStorefrontHash}
                />
            )}
        </Dialog>
    )
}

// The three tabs are alternative layout sources, so the initial tab follows
// whichever one is already set.
function initialTab(
    referenceUrl: string | undefined,
    sourceStorefrontHash: string | undefined,
    matchesInspiration: boolean,
): string {
    if (sourceStorefrontHash) return 'storefront'
    if (referenceUrl && !matchesInspiration) return 'reference'
    return 'inspiration'
}

function confirmLabel(tab: string): string {
    return tab === 'storefront' ? 'Use storefront' : 'Use reference'
}

function TemplatePickerContent({
    onOpenChange,
    onSelected,
    currentReferenceUrl,
    currentSourceStorefrontHash,
}: Omit<TemplatePickerModalProps, 'open'>) {
    const layoutQuery = useLayoutReferencesQuery()
    const layoutReferences = layoutQuery.data ?? []
    const [selectedInspirationId, setSelectedInspirationId] = useState<number>()
    const [referenceUrl, setReferenceUrl] = useState(currentReferenceUrl ?? '')
    const storefrontsQuery = useLandingPagesQuery()
    const storefronts = storefrontsQuery.data ?? []
    const [storefrontSearch, setStorefrontSearch] = useState('')
    const [selectedHash, setSelectedHash] = useState<string | undefined>(
        currentSourceStorefrontHash,
    )
    const matchingInspiration = layoutReferences.find(
        (row) => row.url === currentReferenceUrl,
    )
    const [tab, setTab] = useState<string>()
    const activeTab =
        tab ??
        initialTab(
            currentReferenceUrl,
            currentSourceStorefrontHash,
            !!matchingInspiration,
        )
    const effectiveInspirationId =
        selectedInspirationId ?? matchingInspiration?.id

    const storefrontQuery = storefrontSearch.trim().toLowerCase()
    const matchingStorefronts = storefrontQuery
        ? storefronts.filter((page) =>
              page.name?.toLowerCase().includes(storefrontQuery),
          )
        : storefronts
    // Newest first: the storefront worth remixing is usually a recent one.
    const filteredStorefronts = [...matchingStorefronts].sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )

    const canConfirm = (() => {
        switch (activeTab) {
            case 'inspiration':
                return !!effectiveInspirationId
            case 'reference':
                return referenceUrl.trim().length > 0
            default:
                return !!selectedHash
        }
    })()

    const handleConfirm = () => {
        if (activeTab === 'inspiration') {
            const row = layoutReferences.find(
                (item) => item.id === effectiveInspirationId,
            )
            if (!row) return
            onSelected({ kind: 'referenceUrl', url: row.url, title: row.title })
        } else if (activeTab === 'reference') {
            const url = referenceUrl.trim()
            if (!url) return
            onSelected({ kind: 'referenceUrl', url })
        } else {
            const page = storefronts.find(
                (p) => p.landingPageHash === selectedHash,
            )
            if (!page) return
            onSelected({
                kind: 'sourceStorefront',
                landingPageHash: page.landingPageHash,
                name: page.name,
            })
        }
        onOpenChange(false)
    }

    return (
        <DialogContent className="flex max-h-[70vh] max-w-2xl flex-col overflow-hidden">
            <DialogHeader>
                <DialogCloseButton />
                <DialogTitle>Pick a layout</DialogTitle>
                <DialogDescription>
                    Pick a page from another brand for inspiration, paste a
                    reference URL, or build from an existing storefront.
                </DialogDescription>
            </DialogHeader>

            <Tabs
                value={activeTab}
                onValueChange={setTab}
                className="flex min-h-0 flex-1 flex-col"
            >
                <TabsList className="w-full">
                    <TabsTrigger value="inspiration" className="flex-1 gap-1.5">
                        <Sparkles size={14} />
                        Inspiration
                    </TabsTrigger>
                    <TabsTrigger value="reference" className="flex-1 gap-1.5">
                        <Globe size={14} />
                        Reference URL
                    </TabsTrigger>
                    <TabsTrigger value="storefront" className="flex-1 gap-1.5">
                        <Store size={14} />
                        Storefront
                    </TabsTrigger>
                </TabsList>

                <TabsContent
                    value="inspiration"
                    className="mt-3 flex min-h-0 flex-1 flex-col gap-3"
                >
                    <InspirationTab
                        rows={layoutReferences}
                        isLoading={layoutQuery.isLoading}
                        isError={layoutQuery.isError}
                        selectedId={effectiveInspirationId}
                        onSelectedIdChange={setSelectedInspirationId}
                    />
                </TabsContent>

                <TabsContent
                    value="reference"
                    className="mt-3 flex flex-col gap-3"
                >
                    <Input
                        placeholder="https://example.com/landing-page"
                        value={referenceUrl}
                        onChange={(e) => setReferenceUrl(e.target.value)}
                    />
                    <XSmall className="text-muted-foreground">
                        Paste a URL of a page you want to use as structural
                        inspiration. The AI will analyze its layout, conversion
                        strategy, and section structure to build a similar
                        storefront for your product.
                    </XSmall>
                </TabsContent>

                <TabsContent
                    value="storefront"
                    className="mt-3 flex min-h-0 flex-1 flex-col gap-3"
                >
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search storefronts..."
                            value={storefrontSearch}
                            onChange={(e) =>
                                setStorefrontSearch(e.target.value)
                            }
                            className="pl-9"
                        />
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {storefrontsQuery.isLoading ? (
                            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                                Loading storefronts...
                            </div>
                        ) : filteredStorefronts.length === 0 ? (
                            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                                {storefrontSearch
                                    ? 'No storefronts match your search'
                                    : 'No storefronts found'}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5 py-2">
                                {filteredStorefronts.map((page) => (
                                    <button
                                        key={page.landingPageHash}
                                        type="button"
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                                            selectedHash ===
                                                page.landingPageHash
                                                ? 'border-foreground/40 bg-muted'
                                                : 'border-border hover:bg-muted/50',
                                        )}
                                        onClick={() =>
                                            setSelectedHash(
                                                page.landingPageHash,
                                            )
                                        }
                                    >
                                        <Store
                                            size={16}
                                            className="shrink-0 text-muted-foreground"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium">
                                                {page.name}
                                            </div>
                                            <div className="line-clamp-1 text-xs text-muted-foreground">
                                                {new Date(
                                                    page.createdAt,
                                                ).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <XSmall className="text-muted-foreground">
                        The AI reads this storefront&apos;s structure and writes
                        fresh instructions for each section, then rebuilds it
                        for your ad and products.
                    </XSmall>
                </TabsContent>
            </Tabs>

            <DialogFooter className="border-t pt-4 sm:justify-between">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button disabled={!canConfirm} onClick={handleConfirm}>
                    {confirmLabel(activeTab)}
                </Button>
            </DialogFooter>
        </DialogContent>
    )
}

function InspirationTab({
    rows,
    isLoading,
    isError,
    selectedId,
    onSelectedIdChange,
}: {
    rows: LayoutReference[]
    isLoading: boolean
    isError: boolean
    selectedId: number | undefined
    onSelectedIdChange: (id: number) => void
}) {
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState('all')
    const filtered = search.trim()
        ? rows.filter((row) => {
              const q = search.toLowerCase()
              return (
                  row.title.toLowerCase().includes(q) ||
                  row.brand.toLowerCase().includes(q) ||
                  row.url.toLowerCase().includes(q)
              )
          })
        : rows
    const visible =
        category === 'all'
            ? filtered
            : filtered.filter((row) => row.category === category)
    const groups = groupLayoutReferencesByCategory(visible)
    const categoryCounts = filtered.reduce(
        (counts, row) => {
            counts[row.category] = (counts[row.category] ?? 0) + 1
            return counts
        },
        {} as Record<string, number>,
    )
    const chipCategories = Object.keys(categoryCounts).sort((a, b) =>
        a.localeCompare(b),
    )

    return (
        <>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search title, brand, or URL..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                />
            </div>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className={cn(
                        'rounded-full border px-2.5 py-1 text-xs',
                        category === 'all'
                            ? 'border-foreground/40 bg-muted'
                            : 'border-border text-muted-foreground hover:bg-muted/50',
                    )}
                    onClick={() => setCategory('all')}
                >
                    All
                </button>
                {chipCategories.map((item) => (
                    <button
                        key={item}
                        type="button"
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                            category === item
                                ? 'border-foreground/40 bg-muted'
                                : 'border-border text-muted-foreground hover:bg-muted/50',
                        )}
                        onClick={() => setCategory(item)}
                    >
                        {item}
                        <span className="text-muted-foreground">
                            {categoryCounts[item]}
                        </span>
                    </button>
                ))}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        Loading references...
                    </div>
                ) : isError ? (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        Failed to load references
                    </div>
                ) : groups.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        {search || category !== 'all'
                            ? 'No references match your filters'
                            : 'No inspiration pages yet'}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 py-2">
                        {groups.map((group) => (
                            <div
                                key={group.category}
                                className="flex flex-col gap-2"
                            >
                                {category === 'all' && (
                                    <div className="sticky top-0 z-10 py-1 text-xs font-medium bg-background text-muted-foreground">
                                        {group.category}
                                    </div>
                                )}
                                {group.items.map((row) => (
                                    <div
                                        key={row.id}
                                        className={cn(
                                            'rounded-lg border p-3 transition-colors',
                                            selectedId === row.id
                                                ? 'border-foreground/40 bg-muted'
                                                : 'border-border hover:bg-muted/50',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            className="flex w-full items-start gap-3 text-left"
                                            onClick={() =>
                                                onSelectedIdChange(row.id)
                                            }
                                        >
                                            <Sparkles
                                                size={16}
                                                className="mt-0.5 shrink-0 text-muted-foreground"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium">
                                                    {row.title}
                                                </div>
                                                <div className="line-clamp-1 text-xs text-muted-foreground">
                                                    {row.brand}
                                                    {' · '}
                                                    {row.format}
                                                </div>
                                            </div>
                                        </button>
                                        <a
                                            href={row.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 flex items-center gap-1 truncate pl-7 text-xs text-muted-foreground hover:text-foreground"
                                        >
                                            <span className="truncate">
                                                {row.url}
                                            </span>
                                            <ExternalLink
                                                size={12}
                                                className="shrink-0"
                                            />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    )
}
